import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getSession, textToSpeech } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Trophy, AlertTriangle, CheckCircle, ArrowRight, Volume2, Home, RotateCcw } from 'lucide-react';
import { motion } from 'framer-motion';
import { playHighScoreSound, playScoreSound } from '../lib/sounds';

export default function FeedbackPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ttsPlaying, setTtsPlaying] = useState(false);

  useEffect(() => {
    // Check if we got feedback directly from navigation state
    const navFeedback = location.state?.feedback;
    if (navFeedback && navFeedback.score_total != null && !location.state?.loading) {
      setFeedback(navFeedback);
      setLoading(false);
      try {
        if (navFeedback.score_total >= 70) playHighScoreSound();
        else playScoreSound();
      } catch {}
    } else {
      // Poll for session completion (scoring happens async)
      pollSession();
    }
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const pollSession = async () => {
    setLoading(true);
    let attempts = 0;
    const maxAttempts = 30; // 30 seconds max
    const poll = async () => {
      try {
        const { data } = await getSession(sessionId);
        if (data.status === 'completed' && data.score_total != null) {
          setFeedback({ score_total: data.score_total, label: data.label, feedback: data.feedback });
          setLoading(false);
          try {
            if (data.score_total >= 70) playHighScoreSound();
            else playScoreSound();
          } catch {}
          return;
        }
      } catch {}
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(poll, 1000);
      } else {
        setLoading(false);
      }
    };
    poll();
  };

  const playFeedbackAudio = async () => {
    const fb = feedback?.feedback;
    if (!fb) return;
    const text = `Score total: ${feedback.score_total} sur 100. Label: ${feedback.label}. ${fb.conseil_reformulation || ''}`;
    try {
      setTtsPlaying(true);
      const { data } = await textToSpeech(text, 'nova');
      const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
      audio.onended = () => setTtsPlaying(false);
      audio.play();
    } catch {
      setTtsPlaying(false);
    }
  };

  const getLabelColor = (label) => {
    const colors = {
      'KO': '#E61C24', 'Faible': '#E61C24', 'Passable': '#FFF200',
      'Prometteur': '#00AEEF', 'Convaincant': '#39FF14', 'Expert': '#39FF14'
    };
    return colors[label] || '#FFF200';
  };

  const getGradeClass = (label) => {
    const grades = { 'KO': 'F', 'Faible': 'D', 'Passable': 'C', 'Prometteur': 'B', 'Convaincant': 'A', 'Expert': 'S' };
    return grades[label] || 'C';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center">
        <div className="font-heading text-3xl text-[#FFF200] drop-shadow-[0_0_10px_rgba(255,242,0,0.6)] mb-4">
          GAME OVER
        </div>
        <div className="font-mono text-xl text-[#00AEEF] animate-pulse">
          CALCUL DU SCORE...
        </div>
        <div className="mt-6 flex gap-2">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className="w-3 h-3 bg-[#E61C24] rounded-full animate-bounce"
              style={{ animationDelay: `${i * 0.2}s` }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center p-6">
        <div className="font-mono text-lg text-[#71717A] mb-4">Score non disponible</div>
        <button onClick={() => navigate('/dashboard')} className="font-heading text-sm uppercase text-[#00AEEF] border border-[#00AEEF] px-6 py-2">
          RETOUR
        </button>
      </div>
    );
  }

  const fb = feedback?.feedback || {};
  const details = fb.details || {};
  const scoreTotal = feedback?.score_total || 0;
  const label = feedback?.label || 'KO';
  const labelColor = getLabelColor(label);
  const grade = getGradeClass(label);

  return (
    <div className="min-h-screen bg-[#050505] text-white pb-8">
      {/* Header */}
      <div className="bg-black border-b-4 border-gray-800 p-4 text-center">
        <h1 className="font-heading text-3xl uppercase tracking-widest text-[#FFF200] drop-shadow-[0_0_10px_rgba(255,242,0,0.6)]">
          GAME OVER
        </h1>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Score Display */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', duration: 0.8 }}
          className="bg-black border-4 border-gray-800 shadow-[inset_0_0_30px_rgba(0,0,0,1)] p-8 text-center"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="font-mono text-8xl drop-shadow-[0_0_20px]"
            style={{ color: labelColor, filter: `drop-shadow(0 0 20px ${labelColor})` }}
          >
            {grade}
          </motion.div>
          <div className="font-mono text-5xl text-white mt-2">
            {scoreTotal}<span className="text-2xl text-[#A1A1AA]">/100</span>
          </div>
          <div className="font-heading text-xl uppercase tracking-wider mt-2" style={{ color: labelColor }}>
            {label}
          </div>
        </motion.div>

        {/* Audio Feedback */}
        <button
          data-testid="play-feedback-audio-btn"
          onClick={playFeedbackAudio}
          className="w-full bg-transparent border-2 border-[#00AEEF] text-[#00AEEF] hover:bg-[#00AEEF] hover:text-black font-heading text-lg uppercase tracking-wider py-3 transition-all shadow-[inset_0_0_10px_rgba(0,174,239,0.3)] hover:shadow-[0_0_20px_rgba(0,174,239,0.8)] text-center flex items-center justify-center gap-2"
        >
          <Volume2 size={20} className={ttsPlaying ? 'animate-pulse' : ''} />
          {ttsPlaying ? 'LECTURE EN COURS...' : 'ECOUTER LE FEEDBACK'}
        </button>

        {/* Score Breakdown */}
        <div className="bg-[#121214] border-2 border-[#E61C24]/50 shadow-[inset_0_0_20px_rgba(230,28,36,0.15)] p-4">
          <h2 className="font-heading text-lg uppercase tracking-wider text-[#00AEEF] mb-3">Détail du Score</h2>
          <div className="space-y-2">
            {Object.entries(details).map(([key, val]) => {
              if (!val || typeof val !== 'object') return null;
              const pct = (val.score / val.max) * 100;
              const barColor = pct >= 70 ? '#39FF14' : pct >= 40 ? '#FFF200' : '#E61C24';
              return (
                <div key={key}>
                  <div className="flex justify-between font-mono text-sm">
                    <span className="text-[#A1A1AA] uppercase">{key}</span>
                    <span style={{ color: barColor }}>{val.score}/{val.max}</span>
                  </div>
                  <div className="w-full h-2 bg-black border border-gray-800 mt-1">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.8, delay: 0.3 }}
                      className="h-full"
                      style={{ backgroundColor: barColor }}
                    />
                  </div>
                  {val.comment && (
                    <p className="font-body text-xs text-[#71717A] mt-1">{val.comment}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Errors */}
        {fb.erreurs_detectees?.length > 0 && (
          <div className="bg-[#E61C24]/10 border-2 border-[#E61C24]/50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="text-[#E61C24]" size={18} />
              <h3 className="font-heading text-sm uppercase tracking-wider text-[#E61C24]">Erreurs détectées</h3>
            </div>
            <ul className="space-y-1">
              {fb.erreurs_detectees.map((err, i) => (
                <li key={i} className="font-body text-sm text-[#FF333B] flex items-start gap-2">
                  <span className="text-[#E61C24]">&bull;</span> {err}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Strengths */}
        {fb.points_forts?.length > 0 && (
          <div className="bg-[#39FF14]/10 border-2 border-[#39FF14]/50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="text-[#39FF14]" size={18} />
              <h3 className="font-heading text-sm uppercase tracking-wider text-[#39FF14]">Points forts</h3>
            </div>
            <ul className="space-y-1">
              {fb.points_forts.map((pt, i) => (
                <li key={i} className="font-body text-sm text-[#39FF14] flex items-start gap-2">
                  <span>&bull;</span> {pt}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Reformulation */}
        {fb.conseil_reformulation && (
          <div className="bg-[#00AEEF]/10 border-2 border-[#00AEEF]/50 p-4">
            <h3 className="font-heading text-sm uppercase tracking-wider text-[#00AEEF] mb-2">Conseil de reformulation</h3>
            <p className="font-body text-sm text-white">{fb.conseil_reformulation}</p>
          </div>
        )}

        {/* Ideal Script */}
        {fb.argumentaire_ideal && (
          <div className="bg-[#FFF200]/10 border-2 border-[#FFF200]/50 p-4">
            <h3 className="font-heading text-sm uppercase tracking-wider text-[#FFF200] mb-2">Argumentaire idéal</h3>
            <p className="font-body text-sm text-white">{fb.argumentaire_ideal}</p>
          </div>
        )}

        {/* Punchline */}
        {fb.punchline_suggestion && (
          <div className="bg-[#121214] border-2 border-[#FFF200]/30 p-4 text-center">
            <h3 className="font-heading text-sm uppercase tracking-wider text-[#FFF200] mb-2">Punchline</h3>
            <p className="font-mono text-lg text-[#FFF200] drop-shadow-[0_0_8px_rgba(255,242,0,0.4)]">"{fb.punchline_suggestion}"</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            data-testid="retry-btn"
            onClick={() => navigate(-2)}
            className="flex-1 bg-transparent border-2 border-[#00AEEF] text-[#00AEEF] hover:bg-[#00AEEF] hover:text-black font-heading text-sm uppercase tracking-wider py-3 transition-all text-center flex items-center justify-center gap-2"
          >
            <RotateCcw size={16} /> RETRY
          </button>
          <button
            data-testid="home-btn"
            onClick={() => navigate('/dashboard')}
            className="flex-1 bg-[#E61C24] hover:bg-[#FF333B] text-white font-heading text-sm uppercase tracking-wider py-3 border-b-2 border-red-900 active:border-b-0 active:translate-y-0.5 transition-all text-center flex items-center justify-center gap-2"
          >
            <Home size={16} /> MENU
          </button>
        </div>
      </div>
    </div>
  );
}
